// Snapshots — timeline of CRM scan snapshots with rotting-count trend.

import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Camera, TrendingDown } from 'lucide-react';

import { fetchSnapshots } from '../api';

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface SnapshotsProps {
  integrationID: string;
}

export function Snapshots({ integrationID }: SnapshotsProps) {
  const snapshots = useQuery({
    queryKey: ['pipeline-rot', 'snapshots', integrationID],
    queryFn: () => fetchSnapshots(integrationID),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Camera size={20} className="text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold text-foreground">Scan Snapshots</h1>
          <p className="text-sm text-muted-foreground">
            Historical CRM scans — each row is one pipeline scan run.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Snapshot Timeline</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {snapshots.isLoading && (
            <div className="space-y-3 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {snapshots.isError && (
            <div className="p-6 text-sm text-destructive">
              Failed to load snapshots. {(snapshots.error as Error).message}
            </div>
          )}
          {snapshots.data && snapshots.data.snapshots.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No snapshots yet. Scans will appear here once the worker has run.
            </div>
          )}
          {snapshots.data && snapshots.data.snapshots.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border/40 text-xs text-muted-foreground">
                    <th className="py-2 px-4">Scanned at</th>
                    <th className="py-2 px-4">Provider</th>
                    <th className="py-2 px-4">Total deals</th>
                    <th className="py-2 px-4">Rotting</th>
                    <th className="py-2 px-4">Rot rate</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.data.snapshots.map((s) => {
                    const rate =
                      s.deal_count > 0
                        ? Math.round((s.rotting_count / s.deal_count) * 100)
                        : 0;
                    return (
                      <tr
                        key={s.id}
                        className="border-b border-border/30 hover:bg-muted/20 transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-foreground">
                          {fmtDateTime(s.scanned_at)}
                        </td>
                        <td className="py-3 px-4 text-sm capitalize text-muted-foreground">
                          {s.provider}
                        </td>
                        <td className="py-3 px-4 text-sm text-foreground">{s.deal_count}</td>
                        <td className="py-3 px-4">
                          {s.rotting_count > 0 ? (
                            <span className="flex items-center gap-1 text-sm text-red-400">
                              <TrendingDown size={13} />
                              {s.rotting_count}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
